import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import Alert from 'antd/lib/alert';
import App from 'antd/lib/app';
import Button from 'antd/lib/button';
import DatePicker from 'antd/lib/date-picker';
import Empty from 'antd/lib/empty';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Skeleton from 'antd/lib/skeleton';
import Space from 'antd/lib/space';
import dayjs, { Dayjs } from 'dayjs';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PageHeader } from '../../components/PageHeader';
import { EventStatisticWidget } from '../../components/price-response-reporting/EventStatisticWidget';
import { SavingsWidget } from '../../components/price-response-reporting/SavingsWidget';
import { PriceTriggersWidget } from '../../components/price-response-reporting/PriceTriggersWidget';
import { useCompanies } from '../../components/selectors/GlobalCompanySelector/CompanyContext';
import { useDocumentTitle } from '../../components/useDocumentTitle';
import { Market } from '../../domain/market-prices/interface';
import { PRICE_RESPONSE_MARKETS_DATA } from '../../domain/price-response/interface';
import { usePriceResponseReportQuery } from '../../domain/price-response/queries';
import { exportPriceResponse } from '../../domain/price-response/reporting';
import { IDateRange } from '../interface';
import { usePageLocation } from '../usePageState';
import './priceResponseReporting.css';
import { PriceResponseMarket } from '../priceResponse/view/scenarios/PriceResponseMarket';
import { usePriceListQuery } from '../../domain/market-prices/queries';
import { useMarketsListQuery } from '../../domain/filter-options/queries';
import { PricesWidget } from '../../components/price-response-reporting/PricesWidget';
import { getPresets } from '../../components/selectors/DatePeriodSelectorWithPresets/DatePeriodSelectorWithPresets';
import { sortBy } from 'lodash';
import { marketLoadZoneToLabel } from '../../components/site/SiteMarketAndLoadZone';
import { useAuth } from '../../domain/auth/useAuth';

const SELECT_WIDTH = 200;

export const PriceResponseReporting = () => {
    const { notification } = App.useApp();
    const location = useLocation();
    const { setPageQuery, queryToState } = usePageLocation();
    const pageState: any = queryToState(location.search);
    const { user } = useAuth()!;

    useDocumentTitle('Price Response Reporting');

    const { companyId } = useCompanies()!;
    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [market, setMarket] = useState<Market | undefined>(pageState?.market);
    const [zone, setZone] = useState<string | undefined>(pageState?.zone);

    // pageState comes back from the URL as strings, so parse into real Dates
    // to match IDateRange. Guard against undefined/invalid values.
    const [date, setDate] = useState<IDateRange>({
        start: pageState?.start ? new Date(pageState.start) : (undefined as unknown as Date),
        end: pageState?.end ? new Date(pageState.end) : (undefined as unknown as Date),
    });

    const { data: marketsData, isLoading: isMarketsLoading, isError: isMarketsError } = useMarketsListQuery({ company_id: companyId });
    const priceResponseMarketOptions = sortBy(
        marketsData
            ?.filter(item => Object.keys(PRICE_RESPONSE_MARKETS_DATA).includes(item.market.toUpperCase()))
            .map(item => ({ label: item.market.toUpperCase(), value: item.market.toLowerCase() })) || [],
        'label'
    );

    if (isMarketsError) {
        notification.error({ key: 'site-markets', message: 'Cannot get markets!' });
    }

    const zoneOptions = market
        ? sortBy(
              (PRICE_RESPONSE_MARKETS_DATA[market.toUpperCase()] || []).map(zone => ({ label: marketLoadZoneToLabel(market, zone), value: zone })),
              'label'
          )
        : [];

    const filters = {
        companyId: companyId!,
        market: market!,
        zone: zone!,
        start: date.start,
        end: date.end,
    };

    const {
        data: priceResponseMetrics,
        isLoading,
        isError,
        error,
    } = usePriceResponseReportQuery(filters, {
        enabled: !!companyId && !!market && !!zone && !!date.start && !!date.end,
    });

    if (isError) {
        notification.error({
            key: 'price-response-reporting-error',
            message: error?.message || 'Cannot fetch price response reporting data',
        });
    }

    const triggers = useMemo(() => {
        if (!priceResponseMetrics?.triggers?.length) return [];

        return priceResponseMetrics.triggers.filter(i => !!i.trigger).map(i => i.trigger!);
    }, [priceResponseMetrics]);

    const prMarket = useMemo(() => {
        if (!market || !zone) return null;
        return new PriceResponseMarket(market, zone);
    }, [market, zone]);

    const { data: priceData, isFetching } = usePriceListQuery(
        {
            market: market,
            zone: zone,
            start: date.start,
            end: date.end,
        },
        {
            enabled: !!companyId && !!market && !!zone && !!date.start && !!date.end,
            keepPreviousData: true,
            staleTime: 5 * 60 * 1000,
        }
    );

    const rawPrices = useMemo(() => {
        if (!priceData?.length) return [];
        return priceData.flatMap(zoneData => zoneData.prices);
    }, [priceData]);

    const exportPriceResponseAction = async () => {
        try {
            setExportLoading(true);
            await exportPriceResponse({ companyId: companyId!, start: date.start, end: date.end, market: market!, zone: zone! });
        } catch (error: any) {
            notification.error({
                key: 'price-response-export-error',
                message: error.message || 'Cannot export price response reporting data!',
            });
        }
        setExportLoading(false);
    };

    useEffect(() => {
        if (!marketsData || !market) return;
        const stillValid = priceResponseMarketOptions.some(o => o.value === market.toLowerCase());
        if (!stillValid) {
            handleMarketChange(undefined);
        }
    }, [marketsData]);

    const handleMarketChange = (val: any) => {
        setMarket(val as Market);
        setZone(undefined); // reset zone since it depends on market
        setDate({ start: undefined as unknown as Date, end: undefined as unknown as Date }); // reset date range since timezone depends on market
        setPageQuery({ ...pageState, market: val, zone: undefined, start: undefined, end: undefined });
    };

    const handleZoneChange = (val: any) => {
        setZone(val);
        setPageQuery({ ...pageState, zone: val });
    };

    const disabledDate = (current: Dayjs) => (current ? current.isAfter(dayjs(), 'day') : false);

    const handleDateRangeChange = (dates: [Dayjs | null, Dayjs | null] | null) => {
        if (!dates || !dates[0] || !dates[1] || !prMarket) {
            return;
        }

        // Build timezone-agnostic date strings from the calendar day the user
        // picked. Passing a *string* (not a Date) to fromZonedTime makes it
        // read those digits as literal wall-clock time in prMarket.timezone,
        // with zero influence from the browser's own system timezone.
        const startOfDayStr = `${dates[0].format('YYYY-MM-DD')}T00:00:00.000`;
        const endOfDayStr = `${dates[1].format('YYYY-MM-DD')}T23:59:59.999`;

        const selectedDate: IDateRange = {
            start: fromZonedTime(startOfDayStr, prMarket.timezone),
            end: fromZonedTime(endOfDayStr, prMarket.timezone),
        };

        setDate(selectedDate);
        // Serialize as ISO strings for the URL; parsed back with `new Date(...)` on read above.
        setPageQuery({ ...pageState, start: selectedDate.start.toISOString(), end: selectedDate.end.toISOString() });
    };

    // Convert the stored UTC instants back into the market's wall-clock time
    // before handing them to dayjs/RangePicker, so the displayed calendar day
    // matches what the user picked, regardless of the browser's timezone.
    const rangePickerValue = useMemo((): [Dayjs, Dayjs] | undefined => {
        if (!date.start || !date.end || !prMarket) return undefined;
        return [dayjs(toZonedTime(date.start, prMarket.timezone)), dayjs(toZonedTime(date.end, prMarket.timezone))];
    }, [date.start, date.end, prMarket]);

    const PageContent = () => {
        const Content = priceResponseMetrics ? (
            <>
                <PricesWidget
                    prices={rawPrices}
                    triggers={priceResponseMetrics.triggers}
                    market={prMarket!}
                    isLoading={isFetching}
                    start={date.start}
                    end={date.end}
                />
                <SavingsWidget savings={priceResponseMetrics.savings} market={prMarket!} start={date.start} end={date.end} />
                <EventStatisticWidget events={priceResponseMetrics.events} market={prMarket!} start={date.start} end={date.end} />
                <PriceTriggersWidget triggers={triggers} loading={isLoading} />
            </>
        ) : (
            <Row justify="center">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Row>
        );
        return isLoading ? <Skeleton loading={isLoading} paragraph={{ rows: 20 }} active /> : Content;
    };

    return (
        <>
            <PageHeader
                pageTitle="Price Response"
                actions={[
                    <Select
                        key="market-selector"
                        size="large"
                        placeholder="Select market"
                        options={priceResponseMarketOptions}
                        loading={isMarketsLoading}
                        value={market ? market.toLowerCase() : undefined}
                        onChange={handleMarketChange}
                        allowClear
                        style={{ width: SELECT_WIDTH }}
                    />,
                    <Select
                        key="zone-selector"
                        size="large"
                        placeholder="Select zone"
                        options={zoneOptions}
                        value={zone}
                        disabled={!market}
                        showSearch
                        optionFilterProp="label"
                        onChange={handleZoneChange}
                        allowClear
                        style={{ width: SELECT_WIDTH }}
                    />,
                    <DatePicker.RangePicker
                        key="date-range-picker"
                        data-cy="date-range-picker"
                        size="large"
                        format="DD/MMM/YYYY"
                        value={rangePickerValue}
                        onChange={handleDateRangeChange}
                        disabledDate={disabledDate}
                        presets={getPresets(2024)}
                        disabled={!market || !zone}
                    />,
                    <Button
                        size="large"
                        data-cy="download-csv"
                        key="download-csv"
                        icon={<DownloadOutlined />}
                        onClick={exportPriceResponseAction}
                        loading={exportLoading}
                        disabled={!companyId || !market || !zone || !date.start || !date.end}
                    >
                        Export to PDF
                    </Button>,
                ]}
            />
            <Row justify="center">
                <div className="price-response-reporting">
                    {companyId && market && zone && date.start && date.end ? (
                        <PageContent />
                    ) : (
                        <Row justify="center" className="content-background card-style">
                            <Space direction="vertical">
                                <Alert
                                    showIcon
                                    message={`Please select a ${!user?.isCustomer() ? 'company, ' : ''} market, zone and date range to see data!`}
                                    type="warning"
                                />
                                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                            </Space>
                        </Row>
                    )}
                </div>
            </Row>
        </>
    );
};
